import type { ScoutLocksQuery, ScoutLocksQueryVariables } from "generated-graphql/graphql";

import { Chip, Stack } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";
import { prettyPrintDateTime } from "util/dates";

const SCOUT_LOCKS_QUERY = gql(`
  query ScoutLocks($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    scoutLocks(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        conversationId
        holderId
        expiresAt
        active
        description
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
    }
  }
`);

type ScoutLockRow = ScoutLocksQuery["scoutLocks"]["items"][number];

export function ScoutLocks() {
  const columns: OmnisearchGridColDef<ScoutLockRow>[] = useMemo(
    () => [
      {
        field: "conversationId",
        headerName: "Conversation ID",
        flex: 1.3,
        minWidth: 220,
        filterKeyType: "text",
      },
      {
        field: "holderId",
        headerName: "Holder Node",
        flex: 1.1,
        minWidth: 180,
        filterKeyType: "text",
      },
      {
        field: "active",
        headerName: "Active",
        flex: 0.5,
        minWidth: 110,
        filterKeyType: "boolean",
        renderCell({ value }) {
          return (
            <Chip
              size="small"
              label={value ? "Active" : "Expired"}
              color={value ? "success" : "default"}
            />
          );
        },
      },
      {
        field: "expiresAt",
        headerName: "Expires",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.4,
        minWidth: 240,
        filterKeyType: "text",
      },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 0.9,
        minWidth: 150,
        filterKeyType: "text",
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "updatedBy",
        headerName: "Updated By",
        flex: 0.9,
        minWidth: 150,
        filterKeyType: "text",
      },
      {
        field: "id",
        headerName: "Lock ID",
        flex: 1.2,
        minWidth: 220,
        filterKeyType: "text",
      },
    ],
    []
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<ScoutLocksQuery, ScoutLocksQueryVariables, ScoutLockRow>
        isURLDriven
        persistenceKey="observe-scout-locks"
        columns={columns}
        dataQuery={SCOUT_LOCKS_QUERY}
        initialPageSize={50}
        initialSortModel={[{ field: "expiresAt", sort: "desc" }]}
        getItems={(data) => data.scoutLocks.items}
        getCount={(data) => data.scoutLocks.count}
        noDataMessage="No Scout locks found."
        gridSx={{
          minHeight: "500px",
        }}
      />
    </Stack>
  );
}
